import {useApolloClient} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles, Menu, Switch} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {resolveMcpSetupRedirectInfo} from 'extensions/content-store/extensionInstall';
import MCPSetupRedirectModal from 'extensions/content-store/MCPSetupRedirectModal';
import {AgentType, ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import staticURL from 'lib/staticURL';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {
  type EnabledServer,
  useMCPEnabledServersOptional,
} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {
  navigateToTacMcpServersAndOpenToolsModal,
  refreshCachesAfterMcpDirectoryInstall,
} from 'tenants/admin/extensions/mcp/mcpTacSetupRedirectEvents';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

const useStyles = createStyles(() => ({
  serverIconImg: {
    width: 16,
    height: 16,
    objectFit: 'contain',
  },
}));

function McpServerIcon({server, className}: {server: EnabledServer; className: string}): JSX.Element {
  if (server.icon != null) {
    return (
      <img
        src={staticURL(server.icon)}
        alt=""
        className={className}
        {...getTestProps({component: 'PlusMenu', element: 'mcp-server-icon', key: server.id})}
      />
    );
  }
  // TODO(BNCH-151518): fall back to the icon from the MCP `initialize` response.
  return <BenchlingIcon icon="sources" />;
}

/**
 * Combined "+" dropdown menu for file attachment, public data toggle, and MCP server selection.
 */
export default function PlusMenu({
  fileInputRef,
  agentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  agentType: AgentType | null;
  inputDisabled: boolean;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
}): JSX.Element {
  const {classes} = useStyles();
  const apolloClient = useApolloClient();
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const enabledServers = mcpEnabledServers?.enabledServers ?? [];

  const showPublicData = agentType != null && doesAgentTypeSupportPublicData(agentType);
  const hasTenantMcpConfigs = mcpEnabledServers?.hasTenantMcpConfigs ?? false;
  const showMcpSection =
    agentType != null &&
    doesAgentTypeSupportSources(agentType) &&
    setSelectedMcpServerIds != null &&
    hasTenantMcpConfigs;
  const hasMcpServers = showMcpSection && enabledServers.length > 0;
  const showMcpDirectoryEntry = showMcpSection && BenchlingConfig.ENABLE_EXTENSION_DIRECTORY;

  // When undefined, all servers are selected
  const effectiveSelectedIds = selectedMcpServerIds ?? enabledServers.map((s) => s.id);

  const handleToggleServer = (serverId: string): void => {
    if (!setSelectedMcpServerIds) {
      return;
    }
    const isCurrentlySelected = effectiveSelectedIds.includes(serverId);
    if (isCurrentlySelected) {
      setSelectedMcpServerIds(effectiveSelectedIds.filter((id) => id !== serverId));
    } else {
      const newIds = [...effectiveSelectedIds, serverId];
      // If all servers are now selected, reset to undefined (= "all")
      if (newIds.length === enabledServers.length) {
        setSelectedMcpServerIds(undefined);
      } else {
        setSelectedMcpServerIds(newIds);
      }
    }
  };

  const handleAttachClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleManageMCPs = useCallback((): void => {
    navigateToURL('/ai-connectors');
  }, []);

  const handleBrowseMcpDirectory = useCallback((): void => {
    legacyRunModal(ContentStoreModal, {
      extensionType: ExtensionType.MCP_SERVER,
      onInstall: async (installedExtension) => {
        await refreshCachesAfterMcpDirectoryInstall(apolloClient);
        const mcp = resolveMcpSetupRedirectInfo(installedExtension);
        if (mcp == null) {
          return;
        }
        const {serverId, displayName} = mcp;
        legacyRunModal(MCPSetupRedirectModal, {
          extensionId: installedExtension.id,
          displayName,
          onGoToAdminSettings: () =>
            navigateToTacMcpServersAndOpenToolsModal({
              serverId,
              serverName: displayName,
            }),
        }).catch(() => undefined);
      },
    });
  }, [apolloClient]);

  return (
    <Menu
      position="bottom-start"
      withinPortal={true}
      width="max-content"
      styles={{
        item: {padding: 8, minHeight: 32, color: Color.GRAY_900},
        itemIcon: {color: Color.GRAY_600},
        itemRightSection: {marginLeft: 8},
        label: {color: Color.GRAY_600},
      }}
    >
      <Menu.Target>
        <span style={{display: 'inline-block'}}>
          <Button
            aria-label="More options"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={inputDisabled}
            icon="plus"
            size={ButtonSize.MINI}
          />
        </span>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<BenchlingIcon icon="paperclip" />} onClick={handleAttachClick}>
          Add photos and files
        </Menu.Item>
        {showPublicData && (
          <Menu.Item
            icon={<BenchlingIcon icon="globe" />}
            closeMenuOnClick={false}
            rightSection={
              <Switch
                checked={publicDataEnabled}
                disabled={!isOpenAIEnabled}
                onChange={() => {}}
                size="xs"
                styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
              />
            }
            onClick={() => {
              if (isOpenAIEnabled) {
                setPublicDataEnabled(!publicDataEnabled);
              }
            }}
          >
            Public data
          </Menu.Item>
        )}
        {(hasMcpServers || showMcpSection) && <Menu.Divider />}
        {hasMcpServers && (
          <>
            <Menu.Label>MCPs</Menu.Label>
            {enabledServers.map((server) => (
              <Menu.Item
                key={server.id}
                icon={<McpServerIcon server={server} className={classes.serverIconImg} />}
                closeMenuOnClick={false}
                rightSection={
                  <Switch
                    checked={effectiveSelectedIds.includes(server.id)}
                    onChange={() => {}}
                    size="xs"
                    styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                  />
                }
                onClick={() => {
                  handleToggleServer(server.id);
                }}
              >
                {server.name}
              </Menu.Item>
            ))}
          </>
        )}
        {showMcpSection && (
          <>
            <Menu.Item icon={<BenchlingIcon icon="settings" />} onClick={handleManageMCPs}>
              Manage MCPs
            </Menu.Item>
            {showMcpDirectoryEntry && (
              <Menu.Item icon={<BenchlingIcon icon="store" />} onClick={handleBrowseMcpDirectory}>
                Explore Directory
              </Menu.Item>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
